import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Check, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";

interface Calendar {
	id: string;
	name: string;
	primary: boolean;
	accessRole: string;
}

interface CalendarSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSelect: (calendar: Calendar) => void;
	currentCalendarId?: string;
}

export const CalendarSelectionModal: React.FC<CalendarSelectionModalProps> = ({
	isOpen,
	onClose,
	onSelect,
	currentCalendarId,
}) => {
	const [calendars, setCalendars] = useState<Calendar[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isOpen) {
			fetchCalendars();
		}
	}, [isOpen]);

	const fetchCalendars = async () => {
		setLoading(true);
		setError(null);

		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-calendar-list`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to fetch calendars");
			}

			setCalendars(result.data.calendars);
		} catch (error) {
			console.error("Failed to fetch calendars:", error);
			setError("Failed to load calendars. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleCalendarSelect = (calendar: Calendar) => {
		onSelect(calendar);
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-md bg-white">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Calendar className="h-5 w-5" />
						Select Calendar for Availability
					</DialogTitle>
					<DialogDescription>
						Choose which Google Calendar to use for your availability and
						bookings.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{loading && (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="h-6 w-6 animate-spin" />
							<span className="ml-2">Loading calendars...</span>
						</div>
					)}

					{error && (
						<div className="bg-red-50 border border-red-200 rounded-lg p-4">
							<p className="text-red-600 text-sm">{error}</p>
							<Button
								variant="outline"
								size="sm"
								onClick={fetchCalendars}
								className="mt-2"
							>
								Try Again
							</Button>
						</div>
					)}

					{!loading && !error && (
						<div className="space-y-2 max-h-64 overflow-y-auto">
							{calendars.length === 0 ? (
								<p className="text-gray-500 text-center py-4">
									No calendars found. Please check your Google Calendar
									permissions.
								</p>
							) : (
								calendars.map((calendar) => (
									<Card
										key={calendar.id}
										className={`cursor-pointer transition-all hover:shadow-md ${
											currentCalendarId === calendar.id
												? "ring-2 ring-primary-500 bg-primary-50"
												: "hover:bg-gray-50"
										}`}
										onClick={() => handleCalendarSelect(calendar)}
									>
										<CardContent className="p-4">
											<div className="flex items-center justify-between">
												<div className="flex items-center gap-3">
													<Calendar className="h-5 w-5 text-gray-500" />
													<div>
														<p className="font-medium text-gray-900">
															{calendar.name}
														</p>
														<div className="flex items-center gap-2 text-sm text-gray-500">
															{calendar.primary && (
																<span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
																	Primary
																</span>
															)}
															<span className="capitalize">
																{calendar.accessRole}
															</span>
														</div>
													</div>
												</div>
												{currentCalendarId === calendar.id && (
													<Check className="h-5 w-5 text-primary-600" />
												)}
											</div>
										</CardContent>
									</Card>
								))
							)}
						</div>
					)}

					<div className="flex justify-end gap-2 pt-4 border-t">
						<Button variant="outline" onClick={onClose}>
							Cancel
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
